import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Api } from "../../../Utils/Api";

// ──────────────────────────────────────────────────────────────────────────────
// Histórico de auditoria de um usuário. Busca em GET /historico/:id (o backend faz
// proxy pro serviço central de LOGS) e monta um "diff" campo a campo antes→depois.
// ──────────────────────────────────────────────────────────────────────────────

interface LogHistorico {
  id: number;
  acao: string;
  cpf_usuario: string | null;
  usuario_nome: string | null;
  dados_antes: Record<string, unknown> | null;
  dados_depois: Record<string, unknown> | null;
  origem: string | null;
  criado_em: string;
}

interface Props {
  usuarioId: number;
  nome?: string | null;
  onFechar: () => void;
}

// campos que não interessam no diff (técnicos / derivados)
const IGNORAR = new Set([
  "id",
  "entidade_id",
  "data_cadastro",
  "ultimo_login",
]);

const CAMPO_BOOL = new Set(["administrador", "servidor", "zona_rural"]);
const CAMPO_DATA = new Set(["data_nascimento"]);

const LABELS: Record<string, string> = {
  nome: "Nome",
  usuario: "Usuário",
  cpf: "CPF",
  cnpj: "CNPJ",
  email: "E-mail",
  telefone: "Telefone",
  sexo: "Sexo",
  data_nascimento: "Nascimento",
  descricao: "Descrição",
  endereco: "Endereço",
  bairro: "Bairro",
  cidade: "Cidade",
  uf: "UF",
  cep: "CEP",
  complemento: "Complemento",
  zona_rural: "Zona rural",
  localidade_rural: "Localidade rural",
  administrador: "Administrador",
  servidor: "Servidor",
  inativo: "Situação",
  perfil: "Perfil",
  observacoes: "Observações",
  senha: "Senha",
  foto: "Foto",
};

interface Mudanca {
  campo: string;
  label: string;
  de: string;
  para: string;
}

const norm = (v: unknown) => (v === null || v === undefined ? "" : String(v));

function mascaraCpfCnpj(s: string): string {
  const d = s.replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14)
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return s;
}

function dataBR(s: string): string {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString("pt-BR");
}

function valorLegivel(campo: string, v: unknown): string {
  if (campo === "inativo") return v ? "Inativo" : "Ativo";
  if (CAMPO_BOOL.has(campo))
    return ["1", 1, true, "true"].includes(v as any) ? "Sim" : "Não";
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v);
  if (campo === "cpf" || campo === "cnpj") return mascaraCpfCnpj(s);
  if (CAMPO_DATA.has(campo)) return dataBR(s);
  // foto: logs antigos podem ter o nome do arquivo (longo) → mostra "atualizada"
  if (campo === "foto") return s.length > 24 ? "atualizada" : s;
  return s;
}

function calcularMudancas(
  antes: Record<string, unknown> | null,
  depois: Record<string, unknown> | null
): Mudanca[] {
  const a = antes ?? {};
  const d = depois ?? {};
  const chaves = Array.from(new Set([...Object.keys(a), ...Object.keys(d)]));
  const out: Mudanca[] = [];
  for (const k of chaves) {
    if (IGNORAR.has(k)) continue;
    if (norm(a[k]) === norm(d[k])) continue; // não mudou → pula
    out.push({
      campo: k,
      label: LABELS[k] ?? k,
      de: valorLegivel(k, a[k]),
      para: valorLegivel(k, d[k]),
    });
  }
  return out;
}

function estiloAcao(acao: string): { label: string; cor: string } {
  switch (acao.toUpperCase()) {
    case "CREATE":
      return { label: "Criação", cor: "#16a34a" };
    case "UPDATE":
      return { label: "Edição", cor: "#2563eb" };
    case "INATIVAR":
      return { label: "Inativação", cor: "#dc2626" };
    case "REATIVAR":
      return { label: "Reativação", cor: "#0d9488" };
    case "LIBERAR_SISTEMA":
      return { label: "Sistema liberado", cor: "#16a34a" };
    case "REVOGAR_SISTEMA":
      return { label: "Sistema revogado", cor: "#dc2626" };
    default:
      return { label: acao, cor: "#64748b" };
  }
}

function formatarDataHora(s: string): string {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString("pt-BR");
}

export const HistoricoModal: React.FC<Props> = ({ usuarioId, nome, onFechar }) => {
  const [itens, setItens] = useState<LogHistorico[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErro(null);
    Api.get<LogHistorico[]>(`/historico/${usuarioId}`)
      .then((res) => setItens(res.data ?? []))
      .catch((e: any) => {
        const msg =
          e?.response?.data?.error ?? e?.message ?? "Erro ao carregar o histórico";
        setErro(msg);
      })
      .finally(() => setLoading(false));
  }, [usuarioId]);

  return (
    <Dialog
      open
      onClose={onFechar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          background: "linear-gradient(135deg, #2563eb 0%, #4f8ef7 100%)",
          color: "#fff",
        }}
      >
        <HistoryIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
            Histórico de alterações
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
            {nome ? `${nome} · ` : ""}Usuário #{usuarioId}
          </Typography>
        </Box>
        <IconButton onClick={onFechar} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "background.default", p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : erro ? (
          <Alert severity="error">{erro}</Alert>
        ) : itens.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography color="text.secondary">
              Nenhuma alteração registrada para este usuário.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {itens.map((item) => {
              const { label, cor } = estiloAcao(item.acao);
              const autor =
                item.usuario_nome ||
                (item.cpf_usuario ? mascaraCpfCnpj(item.cpf_usuario) : "") ||
                "—";
              const acaoUpper = item.acao.toUpperCase();
              const ehCriacao = acaoUpper === "CREATE";
              const ehSistema =
                acaoUpper === "LIBERAR_SISTEMA" || acaoUpper === "REVOGAR_SISTEMA";
              const nomeSistema = (item.dados_depois as any)?.sistema ?? "—";
              const mudancas =
                ehCriacao || ehSistema
                  ? []
                  : calcularMudancas(item.dados_antes, item.dados_depois);

              return (
                <Box
                  key={item.id}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderLeft: `4px solid ${cor}`,
                    p: 1.75,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                >
                  {/* header do card */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: mudancas.length || ehCriacao || ehSistema ? 1.25 : 0,
                    }}
                  >
                    <Chip
                      label={label}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: `${cor}1a`,
                        color: cor,
                        border: `1px solid ${cor}55`,
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 15 }} />
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
                      >
                        {autor}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 14, color: "primary.main" }} />
                      <Typography
                        sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: "monospace" }}
                      >
                        {formatarDataHora(item.criado_em)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* corpo: o diff */}
                  {ehCriacao ? (
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      Usuário criado.
                    </Typography>
                  ) : ehSistema ? (
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          color: "text.secondary",
                        }}
                      >
                        Sistema
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
                        {nomeSistema}
                      </Typography>
                    </Box>
                  ) : mudancas.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                      Sem alterações de conteúdo.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {mudancas.map((m) => (
                        <Box
                          key={m.campo}
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              minWidth: 110,
                              color: "text.secondary",
                            }}
                          >
                            {m.label}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: "text.disabled",
                                textDecoration: "line-through",
                              }}
                            >
                              {m.de}
                            </Typography>
                            <ArrowForwardIcon
                              sx={{ fontSize: 14, color: "text.disabled" }}
                            />
                            <Typography
                              sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
                            >
                              {m.para}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
